/**
 * Cross-side system lookup and the game-time pump for a `MockEnvironment`.
 *
 * A scenario asks for a system by CLASS (the instance type is inferred, like
 * `Entity.getComponent(SomeComponent)`) or by id, and gets it wherever it lives: a server area's
 * instance when exactly one area has one, otherwise a client's. Game-level systems are attached to
 * EVERY area's process, so an ambiguous match throws naming the areas — `{ area }` picks one — and
 * a missing match throws naming what was asked for. Nothing here ever returns `undefined`;
 * `hasSystem` is the non-throwing question a readiness poll asks.
 *
 * `createProcessResolver` is the one place a target becomes processes: these queries and every
 * environment mixin built on them (`environment-ticks.ts` and the ones after it) resolve through it,
 * so they agree on what `{ system }`, `{ area }`, `{ client }`, `'all'` and an omitted target mean.
 *
 * `pump` delivers game time to the resolved process(es) the way each one's own loop does: a
 * variable-step process gets one `Process.update(seconds)`; a fixed-step one (`fixedTimeStep: true` on its
 * game/area file) gets whole `update(fixedStepSec)` calls, the remainder carried to its next pump — a
 * system that counts updates must not see one long update where its loop would have made several.
 * It touches nothing else: it does not move timers or `performance.now`, and it is not a virtual
 * clock. `gameTimeMs` is the ruler for it — cumulative simulated milliseconds a process has ticked,
 * natural loop and pumped time on ONE meter, armed the moment the process exists so it never reports
 * "time since first read".
 */
import type { Constructor, System } from '@gotti/core';

/** A system reference: its class, or its registered id. */
export type SystemRef<T extends System<any, any> = System<any, any>> = Constructor<T> | string | number;

/**
 * Where a process lives. `client` is either a position in the environment's client list (the
 * deduped, insertion-ordered clients — the same "client 0" the playground runner uses) or a key of
 * `env.clients` (private or public id), which stays stable across remove/rejoin.
 */
export type ProcessWhere = { area: string } | { client: number | string };

/** Which process(es) to pump or meter. `{ system }` means the process that OWNS that system. */
export type ProcessTarget = { system: SystemRef } | ProcessWhere | 'all';

/** The slice of a game process these queries touch (server `gameProcess` and client `process` alike). */
export interface PumpableProcess {
  systemLookup: Record<string | number, System<any, any>>;
  update(delta: number): void;
  nextTickBeforeUpdate(cb: (delta: number) => void): void;
  nextTickAfterUpdate(cb: (delta: number) => void): void;
  /** The step its own loop updates by when it runs fixed-step; `null` (or absent) when variable-step. */
  readonly fixedStepSec?: number | null;
}

/**
 * The queries every `MockEnvironment` answers, mixed into it by `initializer`.
 */
export interface EnvironmentQueries {
  /** ANY registered system: the server area's instance if exactly one area has one, else client 0's. */
  system<T extends System<any, any>>(ref: SystemRef<T>, where?: ProcessWhere): T;
  /**
   * `true` when `system(ref, where)` would resolve — including the ambiguous multi-area case, which
   * `system()` throws on. Readiness waits poll this; `system()` stays strict. An unknown `where`
   * still throws: a place that does not exist is a test bug, not a state to wait out.
   */
  hasSystem(ref: SystemRef, where?: ProcessWhere): boolean;
  /**
   * Delivers `seconds` of game time to the resolved process(es) the way each one's own loop does: one
   * `update(seconds)` on a variable-step process; whole `update(fixedStepSec)` calls on a fixed-step one,
   * the remainder carried to its next pump. A multi-area env needs a target.
   */
  pump(seconds: number, target?: ProcessTarget): void;
  /**
   * Cumulative simulated milliseconds the resolved process has ticked. One clock: a target that names more than one
   * process (`'all'`, with more than one) throws — as every query that reads this clock does.
   */
  gameTimeMs(target?: ProcessTarget): number;
}

/** One meter per process, armed when the process is created. */
export class GameTimeMeters {
  readonly #elapsedMs = new WeakMap<PumpableProcess, { ms: number }>();

  /** Idempotent. The callback re-arms itself: `nextTickBeforeUpdate` is one-shot. */
  arm(process: PumpableProcess): void {
    if (this.#elapsedMs.has(process)) {
      return;
    }
    const state = { ms: 0 };
    this.#elapsedMs.set(process, state);
    const tick = (deltaSeconds: number) => {
      state.ms += deltaSeconds * 1000;
      process.nextTickBeforeUpdate(tick);
    };
    process.nextTickBeforeUpdate(tick);
  }

  read(process: PumpableProcess): number {
    const state = this.#elapsedMs.get(process);
    if (!state) {
      throw new Error('[test-utils] gameTimeMs: this process was never metered — it was not created by this environment.');
    }
    return state.ms;
  }
}

/** The slice of a `MockEnvironment` the resolver reads: its areas and its clients, live. */
export interface EnvironmentShape {
  areas: Record<string, { gameProcess?: PumpableProcess }>;
  clients: Record<string, { process?: PumpableProcess }>;
}

/** The one place that turns a `ProcessTarget`/`ProcessWhere`/`SystemRef` into processes; every environment mixin shares it. */
export interface ProcessResolver {
  /** The environment's area ids, in `env.areas` key order. */
  areaIds(): string[];
  /** The game process of area `id`; throws naming the areas there are when there is none. */
  areaProcess(id: string): PumpableProcess;
  /** A client's process — by position in the deduped, insertion-ordered client list, or by a key of `env.clients`; throws when there is none. */
  clientProcess(which: number | string): PumpableProcess;
  /** Every area that has a game process, as `[areaId, process]`, in `env.areas` key order. */
  serverProcesses(): ReadonlyArray<readonly [string, PumpableProcess]>;
  /** Every client's process, in client-list order. */
  clientProcesses(): PumpableProcess[];
  /** The process a system lives on, by the server-first rule; throws on ambiguity or absence. */
  ownerOf(ref: SystemRef): PumpableProcess;
  /** Every process a target names, in a stable order; a single-area environment may omit the target. */
  resolveTargets(target: ProcessTarget | undefined): PumpableProcess[];
  /**
   * `{ system }` → the owner; `{ area }`/`{ client }` → that one; `'all'`/undefined → as `resolveTargets`. Text for
   * messages: `area "A"`, `client 0`, `area "A", the owner of BossSystem`, `every process (area "A", client 0)`.
   * Resolves exactly as `resolveTargets` does, so it throws where that throws.
   */
  describeTarget(target: ProcessTarget | undefined): string;
  /**
   * The ONE process `target` names, for `query` — a query that reads one game clock (`gameTimeMs`, `waitFor`'s game
   * budget, `holdFor`, `holdUntil`, `pumps.whenStarved`). A target that names more (`'all'`, with more than one
   * process) throws, naming `query` and the processes: a clock read off several would be one of them, silently.
   */
  oneProcess(target: ProcessTarget | undefined, query: string): PumpableProcess;
}

const describeRef = (ref: SystemRef): string => (typeof ref === 'function' ? ref.name || 'anonymous system class' : `id ${JSON.stringify(ref)}`);

const findIn = <T extends System<any, any>>(process: PumpableProcess | undefined, ref: SystemRef<T>): T | undefined => {
  if (!process) return undefined;
  if (typeof ref === 'function') {
    return Object.values(process.systemLookup).find((system): system is T => system instanceof ref);
  }
  return process.systemLookup[ref] as T | undefined;
};

/**
 * Builds the resolver for one environment. `env` is read on every call, so an area or a client added
 * after this was built resolves too. `initializer` builds one and hands it to every environment mixin.
 */
export const createProcessResolver = (env: EnvironmentShape): ProcessResolver => {
  const areaIds = () => Object.keys(env.areas);
  const areaProcess = (id: string): PumpableProcess => {
    const process = env.areas[id]?.gameProcess;
    if (!process) {
      throw new Error(`[test-utils] unknown area ${JSON.stringify(id)} (areas: ${areaIds().join(', ') || 'none'})`);
    }
    return process;
  };
  /** Deduped, insertion-ordered — `env.clients` holds every client under two keys. */
  const clientList = () => [...new Set(Object.values(env.clients))];
  const clientProcess = (which: number | string): PumpableProcess => {
    const client = typeof which === 'number' ? clientList()[which] : env.clients[which];
    if (!client?.process) {
      throw new Error(`[test-utils] unknown client ${JSON.stringify(which)} (${clientList().length} client(s) in this environment)`);
    }
    return client.process;
  };
  const serverProcesses = () => areaIds().map((id) => [id, env.areas[id].gameProcess] as const).filter((entry): entry is readonly [string, PumpableProcess] => Boolean(entry[1]));
  const clientProcesses = () => clientList().map((client) => client.process).filter((process): process is PumpableProcess => Boolean(process));

  /** The process a system lives on, by the server-first rule. */
  const ownerOf = (ref: SystemRef): PumpableProcess => {
    const owners = serverProcesses().filter(([, process]) => findIn(process, ref));
    if (owners.length === 1) return owners[0][1];
    if (owners.length > 1) {
      throw new Error(`[test-utils] ${describeRef(ref)} is registered in more than one area (${owners.map(([id]) => id).join(', ')}); pass { area } to pick one.`);
    }
    const client = clientProcesses().find((process) => findIn(process, ref));
    if (client) return client;
    throw new Error(`[test-utils] no system matches ${describeRef(ref)} in any area (${areaIds().join(', ') || 'none'}) or client (${clientProcesses().length}).`);
  };

  const resolveTargets = (target: ProcessTarget | undefined): PumpableProcess[] => {
    if (target === 'all') return [...serverProcesses().map(([, process]) => process), ...clientProcesses()];
    if (target === undefined) {
      const servers = serverProcesses();
      if (servers.length === 1) return [servers[0][1]];
      if (servers.length === 0) return [clientProcess(0)];
      throw new Error(`[test-utils] this environment has ${servers.length} server areas (${servers.map(([id]) => id).join(', ')}); pass a target — { system }, { area }, { client } or 'all'.`);
    }
    if ('system' in target) return [ownerOf(target.system)];
    if ('area' in target) return [areaProcess(target.area)];
    return [clientProcess(target.client)];
  };

  /** A resolved process as a message names it: its area id, else its position in the client list. */
  const nameOf = (process: PumpableProcess): string => {
    const area = serverProcesses().find(([, candidate]) => candidate === process);
    return area ? `area ${JSON.stringify(area[0])}` : `client ${clientList().findIndex((client) => client.process === process)}`;
  };
  const describeTarget = (target: ProcessTarget | undefined): string => {
    const names = resolveTargets(target).map(nameOf);
    if (target === 'all') return `every process (${names.join(', ') || 'none'})`;
    if (target !== undefined && 'system' in target) return `${names[0]}, the owner of ${describeRef(target.system)}`;
    return names[0];
  };

  const oneProcess = (target: ProcessTarget | undefined, query: string): PumpableProcess => {
    const processes = [...new Set(resolveTargets(target))];
    if (processes.length !== 1) {
      throw new Error(`[test-utils] ${query} reads ONE game clock, and ${describeTarget(target)} names ${processes.length} — name one: { system }, { area } or { client }.`);
    }
    return processes[0];
  };

  return { areaIds, areaProcess, clientProcess, serverProcesses, clientProcesses, ownerOf, resolveTargets, describeTarget, oneProcess };
};

/** Float slack on a whole step: four pumps of 1/60 s must make four 1/60 s steps, not three and a remainder. */
const STEP_SLACK_S = 1e-9;

/**
 * What `createEnvironmentQueries` builds for one environment: the queries its `MockEnvironment` exposes, and
 * the pump's carry, which only `env.pumps.pace` reads — `initializer` hands it there; it is not part of the env.
 */
export interface EnvironmentQueriesBuild {
  queries: EnvironmentQueries;
  /**
   * Seconds this environment's `pump` gave a fixed-step process that its loop has not run yet — the remainder
   * below one whole step, carried to its next pump (0 for a variable-step process, or one never pumped).
   */
  pumpCarrySec(process: PumpableProcess): number;
}

/**
 * Builds the queries for one environment over its resolver. `initializer` calls this once, spreads `queries`
 * onto the `MockEnvironment` it returns and hands `pumpCarrySec` to `createPacingQueries`; `meters` must be the
 * same instance armed at each process's creation.
 */
export const createEnvironmentQueries = (resolver: ProcessResolver, meters: GameTimeMeters): EnvironmentQueriesBuild => {
  /** Per fixed-step process: pumped time not yet delivered as a whole step (may dip a slack's width below 0). */
  const carried = new WeakMap<PumpableProcess, number>();
  const pumpOne = (process: PumpableProcess, seconds: number) => {
    const step = process.fixedStepSec;
    if (step === undefined || step === null) {
      process.update(seconds);
      return;
    }
    if (!(step > 0)) {
      throw new Error(`[test-utils] pump: a fixed-step process must have fixedStepSec > 0 (got ${step})`);
    }
    let owed = (carried.get(process) ?? 0) + seconds;
    while (owed >= step - STEP_SLACK_S) {
      process.update(step);
      owed -= step;
    }
    carried.set(process, owed);
  };

  const queries: EnvironmentQueries = {
    system<T extends System<any, any>>(ref: SystemRef<T>, where?: ProcessWhere): T {
      if (where) {
        const [process] = resolver.resolveTargets(where);
        const found = findIn(process, ref);
        if (!found) {
          throw new Error(`[test-utils] no system matches ${describeRef(ref)} on ${'area' in where ? `area ${JSON.stringify(where.area)}` : `client ${JSON.stringify(where.client)}`}.`);
        }
        return found;
      }
      return findIn(resolver.ownerOf(ref), ref) as T;
    },
    hasSystem(ref, where) {
      if (where) {
        const [process] = resolver.resolveTargets(where);
        return findIn(process, ref) !== undefined;
      }
      // More than one owning area is the ambiguous case `system()` throws on — the system IS registered.
      return resolver.serverProcesses().some(([, process]) => findIn(process, ref) !== undefined)
        || resolver.clientProcesses().some((process) => findIn(process, ref) !== undefined);
    },
    pump(seconds, target) {
      if (!Number.isFinite(seconds) || seconds < 0) {
        throw new Error(`[test-utils] pump: seconds must be a finite number ≥ 0 (got ${seconds})`);
      }
      for (const process of resolver.resolveTargets(target)) {
        pumpOne(process, seconds);
      }
    },
    gameTimeMs(target) {
      return meters.read(resolver.oneProcess(target, 'gameTimeMs'));
    },
  };
  return { queries, pumpCarrySec: (process) => carried.get(process) ?? 0 };
};
